/**
 * @fileoverview Factory for creating appropriate table renderers
 *
 * Automatically selects the best renderer based on:
 * - Data size
 * - Server-side vs client-side
 * - Existing DOM table
 * - Configuration options
 *
 * @module RendererFactory
 */

import { DomTableRenderer } from './renderers/DomTableRenderer.js';
import { DataTablesRenderer } from './renderers/DataTablesRenderer.js';
import { ServerSideRenderer } from './renderers/ServerSideRenderer.js';
import { VirtualScrollRenderer } from './renderers/VirtualScrollRenderer.js';
import { RENDER_MODE, DEFAULTS } from './constants.js';

/**
 * @typedef {import('./types.js').TableOptions} TableOptions
 * @typedef {import('./types.js').RenderContext} RenderContext
 */

/**
 * RendererFactory - Creates appropriate renderer instances
 * @class
 */
export class RendererFactory {
  /**
   * Create the appropriate renderer for given options and data
   * @param {RenderContext} context - Render context
   * @returns {Object} Renderer instance
   */
  static createRenderer(context) {
    const mode = RendererFactory.selectRenderMode(context);
    console.log(`RendererFactory: Selected render mode: ${mode}`);

    switch (mode) {
      case RENDER_MODE.DOM_TABLE:
        return new DomTableRenderer(context);

      case RENDER_MODE.SERVER_SIDE:
        return new ServerSideRenderer(context);

      case RENDER_MODE.VIRTUAL_SCROLL:
        return new VirtualScrollRenderer(context);

      case RENDER_MODE.DATA_TABLES:
      default:
        return new DataTablesRenderer(context);
    }
  }

  /**
   * Determine which render mode to use
   * @param {RenderContext} context - Render context
   * @returns {string} Render mode constant
   */
  static selectRenderMode(context) {
    const { options, container, data } = context;

    // 1. Server-side processing has highest priority
    if (options.serverSide === true) {
      if (!options.ajax) {
        throw new Error('RendererFactory: ajax option required for server-side mode');
      }
      return RENDER_MODE.SERVER_SIDE;
    }

    // 2. Check for existing DOM table
    if (RendererFactory.isDomTable(container)) {
      // DOM table with no AJAX and no data - just enhance existing table
      if (!options.ajax && !options.data) {
        return RENDER_MODE.DOM_TABLE;
      }

      // DOM table with AJAX or data will use DataTables to populate
      // (will use existing headers if available)
    }

    // 3. Check data size for virtual scrolling
    // Only use virtual scroll if columns are configured (required for virtual scroll)
    if (data && Array.isArray(data) && options.columns && options.columns.length > 0) {
      const threshold = options.virtualScrollThreshold || DEFAULTS.VIRTUAL_SCROLL_THRESHOLD;

      if (data.length >= threshold) {
        // Use virtual scrolling for large datasets
        return RENDER_MODE.VIRTUAL_SCROLL;
      }
    }

    // 4. Default to client-side DataTables
    return RENDER_MODE.DATA_TABLES;
  }

  /**
   * Check if container has or is a DOM table
   * @param {HTMLElement} container - Container element
   * @returns {boolean} True if DOM table exists
   */
  static isDomTable(container) {
    if (!container) {
      return false;
    }

    // Container itself is a table
    if (container.tagName === 'TABLE') {
      return true;
    }

    // Container contains a table
    const table = container.querySelector('table');
    return table !== null;
  }

  /**
   * Check if DataTables library is available
   * @returns {boolean} True if DataTables is loaded
   */
  static isDataTablesAvailable() {
    return typeof jQuery !== 'undefined' && typeof jQuery.fn.DataTable !== 'undefined';
  }

  /**
   * Validate that requirements are met for selected render mode
   * @param {string} mode - Render mode
   * @param {RenderContext} context - Render context
   * @throws {Error} If requirements not met
   */
  static validateRenderMode(mode, context) {
    const { options, data } = context;

    switch (mode) {
      case RENDER_MODE.SERVER_SIDE:
        if (!options.ajax) {
          throw new Error('Server-side mode requires ajax option');
        }
        if (!RendererFactory.isDataTablesAvailable()) {
          throw new Error('Server-side mode requires DataTables library');
        }
        break;

      case RENDER_MODE.DATA_TABLES:
      case RENDER_MODE.DOM_TABLE:
        if (!RendererFactory.isDataTablesAvailable()) {
          throw new Error('DataTables renderer requires DataTables library');
        }
        break;

      case RENDER_MODE.VIRTUAL_SCROLL:
        if (!data || !Array.isArray(data)) {
          throw new Error('Virtual scroll mode requires data array');
        }
        if (!options.columns || options.columns.length === 0) {
          throw new Error('Virtual scroll mode requires columns configuration');
        }
        break;
    }
  }

  /**
   * Get recommended render mode for given data size
   * @param {number} rowCount - Number of rows
   * @returns {string} Recommended render mode
   */
  static getRecommendedMode(rowCount) {
    if (rowCount < 50) {
      return RENDER_MODE.DATA_TABLES;
    } else if (rowCount < 10000) {
      return RENDER_MODE.VIRTUAL_SCROLL;
    } else {
      return RENDER_MODE.SERVER_SIDE;
    }
  }

  /**
   * Get performance characteristics for a render mode
   * @param {string} mode - Render mode
   * @returns {Object} Performance info
   */
  static getPerformanceInfo(mode) {
    const info = {
      [RENDER_MODE.DOM_TABLE]: {
        mode: RENDER_MODE.DOM_TABLE,
        name: 'DOM Table',
        optimalRows: '< 1000',
        renderTime: 'Fast (~50ms)',
        memoryUsage: 'Low',
        features: ['Search', 'Sort', 'Pagination'],
        dependencies: ['jQuery', 'DataTables']
      },
      [RENDER_MODE.DATA_TABLES]: {
        mode: RENDER_MODE.DATA_TABLES,
        name: 'DataTables Client-Side',
        optimalRows: '< 1000',
        renderTime: 'Fast (~100ms)',
        memoryUsage: 'Medium',
        features: ['Search', 'Sort', 'Pagination', 'Filtering'],
        dependencies: ['jQuery', 'DataTables']
      },
      [RENDER_MODE.SERVER_SIDE]: {
        mode: RENDER_MODE.SERVER_SIDE,
        name: 'DataTables Server-Side',
        optimalRows: 'Unlimited',
        renderTime: 'Medium (~200ms + network)',
        memoryUsage: 'Very Low',
        features: ['Search', 'Sort', 'Pagination', 'Filtering'],
        dependencies: ['jQuery', 'DataTables', 'Server API']
      },
      [RENDER_MODE.VIRTUAL_SCROLL]: {
        mode: RENDER_MODE.VIRTUAL_SCROLL,
        name: 'Virtual Scrolling',
        optimalRows: '1000 - 10000',
        renderTime: 'Medium (~300ms)',
        memoryUsage: 'Medium-High',
        features: ['Search', 'Sort', 'Virtual Scrolling'],
        dependencies: []
      }
    };

    return info[mode] || null;
  }

  /**
   * Get all available render modes
   * @returns {Array<string>} Array of render mode constants
   */
  static getAvailableModes() {
    return Object.values(RENDER_MODE);
  }

  /**
   * Get detailed comparison of all render modes
   * @returns {Array<Object>} Comparison data
   */
  static compareRenderModes() {
    return RendererFactory.getAvailableModes().map(mode =>
      RendererFactory.getPerformanceInfo(mode)
    );
  }

  /**
   * Suggest optimal configuration for given dataset
   * @param {number} rowCount - Number of rows
   * @param {boolean} hasServer - Whether server-side endpoint available
   * @returns {Object} Suggested configuration
   */
  static suggestConfiguration(rowCount, hasServer = false) {
    const suggestion = {
      rowCount,
      recommendedMode: null,
      options: {},
      reasoning: ''
    };

    if (rowCount < 50) {
      suggestion.recommendedMode = RENDER_MODE.DATA_TABLES;
      suggestion.options = {
        pageLength: 25,
        searching: true,
        ordering: true,
        paging: true
      };
      suggestion.reasoning = 'Small dataset - use full-featured DataTables';

    } else if (rowCount < 1000) {
      suggestion.recommendedMode = RENDER_MODE.DATA_TABLES;
      suggestion.options = {
        pageLength: 50,
        searching: true,
        ordering: true,
        paging: true
      };
      suggestion.reasoning = 'Medium dataset - DataTables handles well with pagination';

    } else if (rowCount < 10000) {
      if (hasServer) {
        suggestion.recommendedMode = RENDER_MODE.SERVER_SIDE;
        suggestion.options = {
          serverSide: true,
          pageLength: 50
        };
        suggestion.reasoning = 'Large dataset with server - use server-side processing for best performance';
      } else {
        suggestion.recommendedMode = RENDER_MODE.VIRTUAL_SCROLL;
        suggestion.options = {
          virtualScrollThreshold: 1000,
          rowHeight: 35,
          scrollHeight: '500px'
        };
        suggestion.reasoning = 'Large dataset - use virtual scrolling for efficient rendering';
      }

    } else {
      if (hasServer) {
        suggestion.recommendedMode = RENDER_MODE.SERVER_SIDE;
        suggestion.options = {
          serverSide: true,
          pageLength: 100
        };
        suggestion.reasoning = 'Very large dataset - server-side processing is required';
      } else {
        suggestion.recommendedMode = RENDER_MODE.VIRTUAL_SCROLL;
        suggestion.options = {
          virtualScrollThreshold: 1000,
          rowHeight: 35,
          scrollHeight: '600px'
        };
        suggestion.reasoning = 'Very large dataset without server - virtual scrolling recommended, but consider server-side';
      }
    }

    return suggestion;
  }
}

export default RendererFactory;
